import type {
  Attempt,
  Task,
  TaskReadoutState,
  TaskStatusResponse,
} from './client';

export const TASK_READOUT_ORDER: TaskReadoutState[] = [
  'running',
  'retrying',
  'ready',
  'waiting',
  'blocked',
  'done',
  'delivered',
];

export type TaskReadout = {
  state: TaskReadoutState;
  queueRank: number;
};

function latestAttempt(attempts: Attempt[]): Attempt | undefined {
  return attempts.at(-1);
}

export function taskAttemptsByID(attempts: Attempt[]): Map<string, Attempt[]> {
  const attemptsByTask = new Map<string, Attempt[]>();
  for (const attempt of attempts) {
    const taskAttempts = attemptsByTask.get(attempt.taskId) ?? [];
    taskAttempts.push(attempt);
    attemptsByTask.set(attempt.taskId, taskAttempts);
  }
  for (const taskAttempts of attemptsByTask.values()) {
    taskAttempts.sort(
      (left, right) =>
        left.createdAt.localeCompare(right.createdAt) ||
        left.attemptId.localeCompare(right.attemptId)
    );
  }
  return attemptsByTask;
}

export function deriveTaskReadouts(
  data: TaskStatusResponse
): Map<string, TaskReadout> {
  const attemptsByTask = taskAttemptsByID(data.attempts);
  const runnableRank = new Map(
    data.runnableTaskIds.map((taskId, index) => [taskId, index])
  );
  const acceptedTaskIDs = new Set(
    data.tasks.filter((task) => task.delivered).map((task) => task.taskId)
  );
  for (const attempt of data.attempts) {
    if (attempt.state === 'completed' && attempt.conclusion === 'accepted') {
      acceptedTaskIDs.add(attempt.taskId);
    }
  }

  const readouts = new Map<string, TaskReadout>();
  for (const task of data.tasks) {
    const attempts = attemptsByTask.get(task.taskId) ?? [];
    const currentAttempt = latestAttempt(attempts);
    let state: TaskReadoutState;
    if (task.delivered) {
      state = 'delivered';
    } else if (currentAttempt && currentAttempt.state !== 'completed') {
      state = 'running';
    } else if (acceptedTaskIDs.has(task.taskId)) {
      state = 'done';
    } else if (runnableRank.has(task.taskId)) {
      state = currentAttempt ? 'retrying' : 'ready';
    } else if (
      task.dependsOn.some((dependencyID) => !acceptedTaskIDs.has(dependencyID))
    ) {
      state = 'waiting';
    } else {
      state = 'blocked';
    }
    readouts.set(task.taskId, {
      state,
      queueRank: runnableRank.get(task.taskId) ?? Number.MAX_SAFE_INTEGER,
    });
  }
  return readouts;
}

export function taskReadoutRank(state: TaskReadoutState): number {
  return TASK_READOUT_ORDER.indexOf(state);
}

export function taskReadoutLabel(state: TaskReadoutState): string {
  return state[0].toUpperCase() + state.slice(1);
}

export function taskByID(tasks: Task[]): Map<string, Task> {
  return new Map(tasks.map((task) => [task.taskId, task]));
}
